"use client";
/* eslint-disable import/no-extraneous-dependencies */
import { useCallback } from "react";
import { useAuth0 } from "auth0-react-v2";

import { useAuth } from "@stadium/services/auth/client";

import { IS_AUTH0_ENABLED, LOGOUT_CALLBACK_ROUTE } from "@stadium/services/auth0/constants";

const REACT_PROJECT_TYPE = "react";

export const onRedirectCallback = (appState, history) => {
  if (process.env.PROJECT_APP_TYPE === REACT_PROJECT_TYPE) {
    history.replace(appState?.returnTo || window.location.pathname);
    return;
  }
  window.location.replace(appState?.returnTo || "/");
};

export const getAuth0ReturnToRoute = ({
  baseRoute = window.location.pathname,
  includeOrigin = false,
  params = {},
  fullUrl = false,
}) => {
  const url = new URL(baseRoute, window.location.origin);

  if (includeOrigin) {
    url.searchParams.append("origin", window.location.pathname);
  }

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return fullUrl ? url.href : url.pathname + url.search;
};

export const logoutUrl = () =>
  getAuth0ReturnToRoute(
    IS_AUTH0_ENABLED
      ? {
          baseRoute: window.location.origin + LOGOUT_CALLBACK_ROUTE,
          params: { returnTo: window.location.pathname + window.location.search },
          fullUrl: true,
        }
      : {
          baseRoute: window.location.pathname + window.location.search,
          params: {},
          fullUrl: true,
        },
  );

export const useAuth0V2 = (() => {
  if (IS_AUTH0_ENABLED) {
    return () => {
      const {
        loginWithRedirect: loginWithRedirectV2,
        getAccessTokenSilently: getAccessTokenSilentlyV2,
        logout: logoutV2,
        ...rest
      } = useAuth0();

      const loginWithRedirect = useCallback(async (opts) => {
        const { redirectUri, appState, ...restOpts } = opts || {};
        return await loginWithRedirectV2({
          authorizationParams: {
            redirect_uri: redirectUri,
            ...restOpts,
          },
          ...(appState && { appState }),
        });
      }, []);

      const getAccessTokenSilently = useCallback(async (opts) => {
        const { redirectUri, ...restOpts } = opts || {};
        return await getAccessTokenSilentlyV2({
          authorizationParams: {
            ...(redirectUri && { redirect_uri: redirectUri }),
            ...restOpts,
          },
        });
      }, []);

      const logout = useCallback(async (opts) => {
        const { client_id, ...restOpts } = opts || {};
        return await logoutV2({
          logoutParams: { ...restOpts },
          clientId: client_id,
        });
      }, []);

      return {
        loginWithRedirect,
        getAccessTokenSilently,
        logout,
        ...rest,
      };
    };
  }
  return useAuth;
})();
